/**
 * Recovers visitors from stale-deploy breakage on the marketing site.
 *
 * When a new build ships, a tab open across the deploy still references the
 * OLD hashed chunk filenames. Requesting a now-deleted chunk returns the SPA
 * `index.html` (HTML), so the browser fails the load with one of:
 *   - `SyntaxError: Unexpected token '<'` (the HTML fallback parsed as JS),
 *   - `ChunkLoadError: Loading chunk N failed`,
 *   - `TypeError: Failed to fetch dynamically imported module` /
 *     `Importing a module script failed`,
 *   - Gatsby's `page resources for <path> not found. Not rendering React`.
 * The visitor is stuck on a broken page until they manually hard-refresh —
 * this was the top marketing error in PostHog (~80 occ / ~43 sessions / 3d),
 * recurring on every deploy.
 *
 * A single reload pulls the current `index.html` (with the new chunk URLs) and
 * fixes it. We guard hard against reload loops: at most one reload per 10s and
 * at most 3 per tab session (persisted in sessionStorage), so a genuinely
 * broken deploy — or a non-stale-deploy error that happens to match — can't
 * spin the tab.
 */

const RELOAD_STATE_KEY = "eden:staleDeployReload";
const RELOAD_COOLDOWN_MS = 10_000;
const MAX_RELOADS_PER_SESSION = 3;

const STALE_DEPLOY_PATTERNS: RegExp[] = [
  /Loading chunk [\w-]+ failed/i,
  /\bChunkLoadError\b/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unexpected token '<'/i,
  /module script.*MIME type of "text\/html"/i,
  /page resources for .* not found/i,
];

function looksLikeStaleDeploy(message: string | undefined | null): boolean {
  if (!message) return false;
  return STALE_DEPLOY_PATTERNS.some((re) => re.test(message));
}

function reloadForRecovery(reason: string): void {
  try {
    const now = Date.now();
    let state: { at: number; count: number } | null = null;
    try {
      state = JSON.parse(sessionStorage.getItem(RELOAD_STATE_KEY) ?? "null");
    } catch {
      state = null;
    }
    // Already reloaded moments ago → the reload didn't fix it (or this isn't a
    // stale deploy). Stop, so the tab never loops.
    if (state && now - state.at < RELOAD_COOLDOWN_MS) return;
    const count = (state?.count ?? 0) + 1;
    if (count > MAX_RELOADS_PER_SESSION) return;
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify({ at: now, count }));
    console.warn(`[stale-deploy] reloading to recover (${reason})`);
    window.location.reload();
  } catch {
    // sessionStorage / location unavailable — nothing we can safely do.
  }
}

export function installStaleDeployRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? "");
    if (looksLikeStaleDeploy(message)) reloadForRecovery("unhandledrejection");
  });

  window.addEventListener("error", (event) => {
    if (looksLikeStaleDeploy(event.message)) reloadForRecovery("error");
  });
}
